import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchStoreProducts } from '../Api/storeApi';
import '../assets/css/search-box.css';

/**
 * Header search with a live suggestions dropdown.
 *
 * Replaces an input that had three separate problems:
 *
 * 1. IT REQUIRED A LOGIN. Typing while signed out redirected to /login — while
 *    the same visitor could already browse every product, every category and
 *    every price. The catalogue is public and served by the unauthenticated
 *    Store API, so gating the search box protected nothing and simply hid the
 *    shop from anyone who did not already know what it stocked.
 *
 * 2. IT NAVIGATED AS YOU TYPED. A 400ms debounce pushed /search-result/<term>
 *    on every pause, so typing one word could stack several history entries and
 *    the back button had to be pressed repeatedly to escape. Clearing the box
 *    navigated to the home page, discarding whatever you were reading.
 *
 * 3. THERE WERE NO SUGGESTIONS. The groundwork existed — Header already
 *    declared isOpen, highlightedIndex, inputRef and listRef — but nothing used
 *    them, so the state sat unread.
 *
 * Now: typing only fetches suggestions. Navigation happens when the person asks
 * for it, by picking a suggestion or pressing Enter.
 */

const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;
const MAX_SUGGESTIONS = 8;

export default function SearchBox({ className = 'green-search-bar' }) {
    const navigate = useNavigate();

    const [term, setTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(-1);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);

    const rootRef = useRef(null);
    const inputRef = useRef(null);

    // Monotonic request id. Responses can arrive out of order — a slow request
    // for "par" landing after a fast one for "parac" would overwrite the newer,
    // more specific results with staler ones. Only the latest request may write.
    const requestRef = useRef(0);

    const close = useCallback(() => {
        setOpen(false);
        setHighlight(-1);
    }, []);

    // --- fetch suggestions -------------------------------------------------

    useEffect(() => {
        const query = term.trim();

        if (query.length < MIN_CHARS) {
            setSuggestions([]);
            setSearched(false);
            setLoading(false);
            return undefined;
        }

        const requestId = ++requestRef.current;
        setLoading(true);

        const timer = setTimeout(async () => {
            try {
                const { data } = await fetchStoreProducts({
                    search: query,
                    per_page: MAX_SUGGESTIONS,
                    page: 1,
                });
                if (requestId !== requestRef.current) return;
                setSuggestions(data);
                setSearched(true);
                setOpen(true);
                setHighlight(-1);
            } catch (error) {
                if (requestId !== requestRef.current) return;
                // A failed suggestion lookup must not block the search itself —
                // Enter still goes to the results page.
                console.error('Search suggestions failed:', error);
                setSuggestions([]);
                setSearched(true);
            } finally {
                if (requestId === requestRef.current) setLoading(false);
            }
        }, DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [term]);

    // --- close when the click lands outside --------------------------------

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) close();
        };
        // `mousedown`, not `click`: a `click` listener fires after the input
        // blurs, which on some browsers tears the dropdown down before the
        // selection it was meant to register.
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open, close]);

    // --- actions -----------------------------------------------------------

    const goToProduct = (product) => {
        close();
        setTerm('');
        navigate(`/product/${product.id}`);
    };

    const goToResults = () => {
        const query = term.trim();
        // An empty submit used to navigate to /search-result/undefined, which
        // then searched for the literal string "undefined".
        if (!query) return;
        close();
        navigate(`/search-result/${encodeURIComponent(query)}`);
    };

    const onKeyDown = (event) => {
        if (event.key === 'Escape') {
            close();
            return;
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            if (!suggestions.length) return;
            event.preventDefault();
            setOpen(true);
            setHighlight((current) => {
                const step = event.key === 'ArrowDown' ? 1 : -1;
                const next = current + step;
                // Wraps, so holding either arrow cycles rather than sticking.
                if (next < 0) return suggestions.length - 1;
                if (next >= suggestions.length) return 0;
                return next;
            });
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            if (open && highlight >= 0 && suggestions[highlight]) {
                goToProduct(suggestions[highlight]);
            } else {
                goToResults();
            }
        }
    };

    const showDropdown = open && term.trim().length >= MIN_CHARS;

    return (
        <div className="searchbox" ref={rootRef}>
            <form
                className={className}
                role="search"
                onSubmit={(event) => {
                    event.preventDefault();
                    goToResults();
                }}
            >
                <input
                    ref={inputRef}
                    type="text"
                    className="search-input"
                    placeholder="Search medicines, health products..."
                    value={term}
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={showDropdown}
                    aria-controls="searchbox-list"
                    aria-autocomplete="list"
                    aria-activedescendant={highlight >= 0 ? `searchbox-option-${highlight}` : undefined}
                    onChange={(event) => {
                        setTerm(event.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => { if (suggestions.length) setOpen(true); }}
                    onKeyDown={onKeyDown}
                />

                <button type="submit" className="search-btn" aria-label="Search">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                        stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                </button>
            </form>

            {showDropdown && (
                <ul className="searchbox__list" id="searchbox-list" role="listbox">
                    {suggestions.map((product, index) => (
                        <li
                            key={product.id}
                            id={`searchbox-option-${index}`}
                            role="option"
                            aria-selected={index === highlight}
                            className={`searchbox__item${index === highlight ? ' is-active' : ''}`}
                            // onMouseDown, not onClick: mousedown fires before
                            // the input's blur, so the selection survives.
                            onMouseDown={(event) => {
                                event.preventDefault();
                                goToProduct(product);
                            }}
                            onMouseEnter={() => setHighlight(index)}
                        >
                            {product.images?.[0]?.src ? (
                                <img
                                    className="searchbox__thumb"
                                    src={product.images[0].src}
                                    alt=""
                                    loading="lazy"
                                    aria-hidden="true"
                                />
                            ) : (
                                <span className="searchbox__thumb searchbox__thumb--empty" aria-hidden="true" />
                            )}

                            <span className="searchbox__text">
                                <span className="searchbox__name">{product.name}</span>
                                <span className="searchbox__meta">
                                    {product.price ? `₹${product.price}` : ''}
                                    {product.stock_status === 'outofstock' && (
                                        <span className="searchbox__oos">Out of stock</span>
                                    )}
                                </span>
                            </span>
                        </li>
                    ))}

                    {/* An empty dropdown and a slow one look identical, so say
                        which it is rather than showing a blank panel. */}
                    {suggestions.length === 0 && (
                        <li className="searchbox__empty" role="presentation">
                            {loading || !searched ? 'Searching…' : `No matches for “${term.trim()}”`}
                        </li>
                    )}

                    {suggestions.length > 0 && (
                        <li
                            className="searchbox__all"
                            role="option"
                            aria-selected={false}
                            onMouseDown={(event) => {
                                event.preventDefault();
                                goToResults();
                            }}
                        >
                            See all results for “{term.trim()}”
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
}
